import {
  LinkButton,
  Dropdown,
  Panel,
  DropdownMenuButton,
  DropdownMenuSplitter,
} from '@epam/promo';
import { DropdownBodyProps, IDropdownTogglerProps } from '@epam/uui-core';
import { ReactComponent as SelectedIcon } from '@epam/assets/icons/common/notification-done-18.svg';
import { ACTIVE_STATUS, USER_ROLE, UserRole } from 'models/user.model';
import { useAdminDeleteQuery, useModal, useUpdateAdminQuery } from 'hooks';
import css from './PermissionControl.module.scss';
import { PermissionControlProps } from './PermissionControl.model';

export const PermissionControl = ({ user }: PermissionControlProps) => {
  const modal = useModal();
  const { mutate: updateAdminRole } = useUpdateAdminQuery();
  const { mutate: deleteAdmin } = useAdminDeleteQuery();
  const roles = Object.keys(USER_ROLE)
    .filter((role) => role !== 'GUEST')
    .map((role) => ({
      id: role,
      value: USER_ROLE[role as UserRole],
    }));

  const renderTarget = ({
    isOpen,
    isDropdown,
    ref,
    onClick,
  }: IDropdownTogglerProps) => (
    <LinkButton
      ref={ref}
      caption={USER_ROLE[user.role]}
      cx={css['permission-control__target']}
      isDropdown={isDropdown}
      isOpen={isOpen}
      size="36"
      onClick={onClick}
    />
  );

  const renderRoles = ({ onClose }: DropdownBodyProps) => (
    <Panel background="white" cx={css['permission-control__roles']}>
      {roles.map((role) => (
        <DropdownMenuButton
          key={role.id}
          caption={role.value}
          cx={
            role.id === user.role
              ? css['permission-control__text']
              : css['permission-control__text-border']
          }
          icon={role.id === user.role ? SelectedIcon : undefined}
          iconPosition="right"
          isDisabled={role.id === user.role}
          onClick={() =>
            modal.confirm({
              text:
                user.status !== ACTIVE_STATUS
                  ? `Are you sure you want to change the user role for ${user.email}?`
                  : `Are you sure you want to change the user role for ${user.firstName} ${user.lastName}?`,
              highlights: [
                `${
                  user.status !== ACTIVE_STATUS
                    ? `${user.email}`
                    : `${user.firstName} ${user.lastName}`
                }`,
                'change',
              ],
              onSuccess: () => {
                updateAdminRole({ id: user.id, role: role.id as UserRole });
                onClose?.();
              },
            })
          }
        />
      ))}
    </Panel>
  );

  const renderBody = (props: DropdownBodyProps) => (
    <Panel shadow background="white" cx={css['permission-control ']}>
      {renderRoles(props)}
      <DropdownMenuSplitter cx={css['permission-control__splitter']} />
      <Panel background="white" cx={css['permission-control__delete']}>
        <DropdownMenuButton
          caption="Delete"
          cx={css['permission-control__text']}
          onClick={() =>
            modal.delete({
              text:
                user.firstName && user.lastName
                  ? `Are you sure you want to revoke permissions of ${user.firstName} ${user.lastName}?`
                  : `Are you sure you want to delete ${user.email}?`,
              highlights: [
                `${
                  user.status !== ACTIVE_STATUS
                    ? user.email
                    : `${user.firstName} ${user.lastName}`
                }`,
                'delete',
                'revoke',
              ],
              onSuccess: () => deleteAdmin({ id: user.id }),
            })
          }
        />
      </Panel>
    </Panel>
  );

  return (
    <Dropdown
      placement="bottom-end"
      renderBody={renderBody}
      renderTarget={renderTarget}
    />
  );
};
